import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { useAppSelector } from "./types";
import tenantApi from "@/api/tenant";
import { setTenant } from "@/store/tenant/slice";
import { getUserDetails } from "@/store/user/selectors";
import { ITenant } from "@/types/tenant";

const ONE_HOUR = 60 * 60 * 1000;

const applyPrimaryTheme = (tenant: ITenant, callback: () => void) => {
    const root = document.documentElement;
    if (!tenant?.theme?.primary || !tenant?.theme?.gradient) {
        callback();
    } else {
        const tenantPrimaryColours = tenant.theme.primary;
        const tenantGradientColours = tenant.theme.gradient;

        Object.entries(tenantPrimaryColours).forEach(([key, rgb]) => {
            if (Array.isArray(rgb) && rgb.length === 3) {
                const cssValue = `${rgb[0]} ${rgb[1]} ${rgb[2]}`;
                root.style.setProperty(`--${key}`, cssValue);
            }
        });

        Object.entries(tenantGradientColours).forEach(([key, hex]) => {
            root.style.setProperty(`--gradient-${key}`, hex);
        });

        callback();
    }
};

export const useTenantData = () => {
    const dispatch = useDispatch();
    const userDetails = useAppSelector(getUserDetails);
    const [searchParams] = useSearchParams();
    const [hasDispatchedTenant, setHasDispatchedTenant] = useState(false);

    const derivedTenantHint = useMemo(() => {
        const fromQuery = searchParams.get("t");
        if (fromQuery) return fromQuery;
        const fromStorage = localStorage.getItem("tenantOverride");
        return fromStorage || undefined;
    }, [searchParams]);

    const tenantQuery = useQuery({
        queryKey: [
            "tenant",
            userDetails ? "me" : "identify",
            derivedTenantHint,
        ],
        queryFn: () =>
            userDetails
                ? tenantApi.getUserTenant()
                : tenantApi.identifyTenant(derivedTenantHint),
        staleTime: ONE_HOUR,
        gcTime: ONE_HOUR,
        retry: 1,
    });

    // Apply theme and dispatch to Redux when tenant data is available
    useEffect(() => {
        if (tenantQuery.isSuccess && tenantQuery.data) {
            applyPrimaryTheme(tenantQuery.data, () => {
                dispatch(setTenant(tenantQuery.data));
                setHasDispatchedTenant(true);
                // Persist last-used override for admin convenience
                if (derivedTenantHint) {
                    localStorage.setItem(
                        "tenantOverride",
                        String(derivedTenantHint),
                    );
                }
            });
        } else if (tenantQuery.isError) {
            // ensure we don't block indefinitely on error
            setHasDispatchedTenant(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        tenantQuery.isSuccess,
        tenantQuery.isError,
        tenantQuery.data,
        dispatch,
    ]);

    const isWaitingForDispatch =
        !tenantQuery.isError && !tenantQuery.isLoading && !hasDispatchedTenant;

    return {
        tenant: tenantQuery.data,
        isLoading: tenantQuery.isLoading || isWaitingForDispatch,
        isError: tenantQuery.isError,
        error: tenantQuery.error,
    };
};
